import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { VOLUNTEER_ACTIVITIES } from "@/lib/constants";
import { PageHead, Panel, SButton, Empty, softDelete } from "@/components/staff/Bits";

const CHECKS = { identity: "Identity verified", suitability: "Suitability assessed", qualifications: "Qualifications checked (where needed)", safeguarding: "Safeguarding requirements met" };

function VolunteerCard({ v, onSaved }) {
  const [checks, setChecks] = useState(v.checks);
  const save = async (status) => {
    try { await api.patch(`/volunteers/${v.id}`, { status, checks }); toast.success("Saved"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel testid={`volunteer-card-${v.id}`}>
      <div className="flex flex-wrap justify-between gap-2"><div><p className="font-bold text-lg">{v.full_name}</p><p className="text-sm text-[#605349]">{[v.contact_number, v.email].filter(Boolean).join(" · ")} · {fmtDate(v.created_at)}</p></div>
        <span data-testid={`volunteer-status-${v.id}`} className={`h-fit rounded-full px-3 py-0.5 text-xs font-bold ${v.status === "approved" ? "bg-[#3D6346] text-white" : v.status === "declined" ? "bg-[#E7DFD5]" : "bg-[#FDF7E7] text-[#7A5A10]"}`}>{v.status}</span></div>
      <p className="mt-3 text-[15px]">{v.activities.map((a) => VOLUNTEER_ACTIVITIES[a]).join(", ")}</p>
      {v.availability && <p className="text-sm text-[#605349]">Availability: {v.availability}</p>}
      {v.qualifications && <p className="text-sm text-[#605349]">Qualifications: {v.qualifications}</p>}
      <div className="mt-4 grid sm:grid-cols-2 gap-2">{Object.entries(CHECKS).map(([k, l]) => (
        <label key={k} className="flex gap-2 text-sm"><input type="checkbox" data-testid={`vol-check-${k}-${v.id}`} className="w-5 h-5 accent-[#3D6346]" checked={checks[k]} onChange={(e) => setChecks({ ...checks, [k]: e.target.checked })} />{l}</label>))}</div>
      <div className="mt-4 flex gap-2">
        <SButton variant="green" data-testid={`vol-approve-${v.id}`} disabled={!Object.values(checks).every(Boolean)} onClick={() => save("approved")}>Approve</SButton>
        <SButton variant="ghost" data-testid={`vol-decline-${v.id}`} onClick={() => save("declined")}>Decline</SButton>
        <SButton variant="ghost" data-testid={`vol-save-${v.id}`} onClick={() => save("pending")}>Save checks</SButton>
        <SButton variant="danger" data-testid={`vol-delete-${v.id}`} onClick={async () => { if (await softDelete("volunteer", v.id, `offer from ${v.full_name}`)) onSaved(); }}>Delete</SButton>
      </div>
    </Panel>
  );
}

export default function Volunteers() {
  const [list, setList] = useState(null);
  const load = useCallback(() => { api.get("/volunteers").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="max-w-4xl">
      <PageHead title="Volunteers & offers of help" sub="No one is approved on submission alone. Verify identity, suitability, qualifications and safeguarding before authorizing participation. Full volunteer task management arrives in Phase 2." />
      {!list ? <Empty>Loading…</Empty> : list.length === 0 ? <Empty testid="volunteers-empty">No offers yet.</Empty> : <div className="space-y-4">{list.map((v) => <VolunteerCard key={v.id} v={v} onSaved={load} />)}</div>}
    </div>
  );
}
